import { useState, useEffect, useContext } from 'react'
import moment from 'moment'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import {
  formatDateTime,
  formatNumber,
  formatCurrency,
  createWorkbook,
  addWorksheet,
  DEFAULTNUMHEADERS,
  saveWorkbook,
  setColumnsAndRows,
  setHeaders,
  numberFormat,
  addEmptyRows,
  styleEmptyRows,
  styleTotalRow,
  getDefaultSeason,
  getTimezoneDate,
  EMPTY_DISPLAY_VALUE,
  checkPermission,
} from '../../utilities/helpers'

import { getEventSalesAndAttendance, getSeasons } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import { ReportActions } from '../ReportActions'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { SeasonSelector } from '../SeasonSelector'

const sumMetric = (rows, key) =>
  rows.reduce((acc, row) => acc + Number(row?.[key] || 0), 0)

/** Whole-ticket counts for Excel (avoids 2045.00-style floats in cells). */
const excelCount = (n) => Math.round(Number(n) || 0)

const formatEventDate = (event) => {
  if (event.isMonthlyTotal) return ''
  if (!event.timezone || !event.rawDate) {
    return event.eventDate ?? EMPTY_DISPLAY_VALUE
  }
  const d = getTimezoneDate(event.rawDate, event.timezone)
  return d ? formatDateTime(d) : EMPTY_DISPLAY_VALUE
}

const sumTicketProceeds = (rows) =>
  rows.reduce((acc, row) => acc + Number(row?.ticketProceeds || 0), 0)

const buildMonthlyTotalRow = (row, rows) => ({
  ...row,
  sold: sumMetric(rows, 'sold'),
  comp: sumMetric(rows, 'comp'),
  total: sumMetric(rows, 'total'),
  scanned: sumMetric(rows, 'scanned'),
  unscanned: sumMetric(rows, 'unscanned'),
  ticketProceeds: sumTicketProceeds(rows),
})

const filterReportRowsBySeason = (rows = [], seasonName) => {
  if (!seasonName) return rows

  const filteredRows = []
  let monthRows = []

  const flushMonth = (monthlyTotalRow = null) => {
    if (monthRows.length === 0) {
      monthRows = []
      return
    }

    filteredRows.push(...monthRows)

    if (monthlyTotalRow) {
      filteredRows.push(buildMonthlyTotalRow(monthlyTotalRow, monthRows))
    }

    monthRows = []
  }

  rows.forEach((row) => {
    if (row?.isMonthlyTotal) {
      flushMonth(row)
      return
    }

    if (row?.seasonName === seasonName) {
      monthRows.push(row)
    }
  })

  flushMonth()

  return filteredRows
}

export default function EventSalesAndAttendanceWrapper({ organization }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_EVENT_SALES_AND_ATTENDANCE
    )

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [seasons, setSeasons] = useState([])
  const [selectedSeason, setSelectedSeason] = useState('')

  const [eventAnalytics, setEventAnalytics] = useState(null)

  useEffect(() => {
    if (!orgPermissionsReady || !hasPermission) return
    if (!organization?.uuid) return
    showLoading()
    loadSeasons()
  }, [orgPermissionsReady, hasPermission, organization?.uuid])

  useEffect(() => {
    const fetchData = async () => {
      const season = seasons?.find(
        (existingSeason) =>
          String(existingSeason?.id) === String(selectedSeason)
      )
      const fromDate = season?.start ? new Date(season.start) : null
      const toDate = season?.ends ? new Date(season.ends) : null

      if (!selectedSeason || !fromDate || !toDate) return

      setIsLoadingData(true)

      try {
        const res = await getEventSalesAndAttendance(
          organization.uuid,
          fromDate?.toISOString() || null,
          toDate?.toISOString() || null
        )

        setEventAnalytics(
          filterReportRowsBySeason(res?.data || [], season?.name || '')
        )
      } catch (error) {
        console.error('Error fetching event sales and attendance:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    fetchData()
  }, [
    selectedSeason,
    seasons,
    organization?.uuid,
    orgPermissionsReady,
    hasPermission,
    refreshToken,
  ])

  const loadSeasons = () => {
    getSeasons(organization.uuid)
      .then((res) => {
        const fetchedSeasons = res?.data || []
        setSeasons(fetchedSeasons)
        const defaultSeason = getDefaultSeason(fetchedSeasons)
        if (defaultSeason?.id) {
          setSelectedSeason(String(defaultSeason.id))
        }
      })
      .catch((error) => console.error(error))
      .finally(() => hideLoading())
  }

  const calculateTotals = () => {
    const totals = {
      ticketsSold: 0,
      compsSold: 0,
      total: 0,
      scanned: 0,
      unScanned: 0,
      ticketProceeds: 0,
    }
    const monthlyTotals =
      eventAnalytics?.filter((event) => event.isMonthlyTotal) || []
    const eventRows =
      eventAnalytics?.filter((event) => !event.isMonthlyTotal) || []
    const sourceRows = monthlyTotals.length > 0 ? monthlyTotals : eventRows

    const sumTotals = (property) => {
      return sourceRows?.reduce(
        (acc, cur) => acc + Number(cur[property] || 0),
        0
      )
    }
    totals.ticketsSold += sumTotals('sold')
    totals.compsSold += sumTotals('comp')
    totals.total += sumTotals('total')
    totals.scanned += sumTotals('scanned')
    totals.unScanned += sumTotals('unscanned')
    totals.ticketProceeds += sumTotals('ticketProceeds')

    return totals
  }

  // Universal Excel Generator using JSON schema
  const handleExcelExport = async (
    organizationName,
    season,
    reportData,
    totals
  ) => {
    const fromDate = season?.start ? new Date(season.start) : null
    const toDate = season?.ends ? new Date(season.ends) : null

    const dateRange =
      fromDate && toDate
        ? `${formatDateTime(moment(fromDate), 'dateOnly')} - ${formatDateTime(moment(toDate), 'dateOnly')}`
        : null

    const workbook = createWorkbook()
    const worksheet = addWorksheet('Sales and Attendance', workbook)

    const format = (data) => {
      const {
        isMonthlyTotal,
        comp,
        eventName,
        seasonName,
        scanned,
        sold,
        ticketProceeds,
        total,
        unscanned,
      } = data

      return {
        event_date: formatEventDate(data),
        event: eventName,
        season: seasonName,
        sold: excelCount(sold),
        comp: excelCount(comp),
        total: excelCount(total),
        scanned: excelCount(scanned),
        unscanned: excelCount(unscanned),
        ticket_proceeds: parseFloat(ticketProceeds),
        is_monthly_total: isMonthlyTotal ?? false,
      }
    }

    const headers = [
      ...(season?.name ? [`Season: ${season.name}`] : []),
      ...(dateRange ? [`Date Range: ${dateRange}`] : []),
    ]

    setHeaders(
      'Sales and Attendance Report',
      organizationName,
      worksheet,
      headers
    )

    setColumnsAndRows(
      Object.keys(format(reportData)),
      Object.values(reportData),
      format,
      worksheet
    )

    const getColNum = (type) => {
      const soldTotalColumn = 'D'
      const compTotalColumn = 'E'
      const totalTotalColumn = 'F'
      const scannedTotalColumn = 'G'
      const unscannedTotalColumn = 'H'
      const ticketProceedsTotalColumn = 'I'

      switch (type) {
        case 'sold':
          return soldTotalColumn
        case 'comp':
          return compTotalColumn
        case 'total':
          return totalTotalColumn
        case 'scanned':
          return scannedTotalColumn
        case 'unscanned':
          return unscannedTotalColumn
        case 'proceeds':
          return ticketProceedsTotalColumn

        default:
          return
      }
    }

    const headerRows = DEFAULTNUMHEADERS + headers?.length

    worksheet.getColumn('is_monthly_total').hidden = true

    const monthlyTotalRows = []

    const getMonthlyTotalCellValue = (row) =>
      row.getCell('is_monthly_total').value

    // style monthly total rows
    // add empty row after monthly total rows
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber <= headerRows) return // Skip title and header rows
      const isMonthlyTotal = getMonthlyTotalCellValue(row)

      if (isMonthlyTotal) {
        styleTotalRow(row, worksheet, true)

        // Mark row to insert a blank row after it
        monthlyTotalRows.push(rowNumber)
      }
    })

    // Insert blank rows
    addEmptyRows(monthlyTotalRows, worksheet)

    // style the empty rows - have to get them again from actual table because of shifting
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber <= headerRows) return // Skip title and header rows
      const isMonthlyTotal = getMonthlyTotalCellValue(row)

      if (isMonthlyTotal) {
        // Determine last column
        const lastColNumber = worksheet.lastColumn.number

        const emptyRow = worksheet.getRow(rowNumber + 1)

        styleEmptyRows(emptyRow, lastColNumber, true)
      }
    })

    // After all rows are processed:
    const lastRowNum = worksheet.lastRow.number
    const totalRow = worksheet.getRow(lastRowNum + 1)
    const totalRowNumber = worksheet.getRow(lastRowNum + 1).number

    // total row
    worksheet.getCell(`B${totalRowNumber}`).value = 'GRAND TOTAL'
    worksheet.getCell(`${getColNum('sold')}${totalRowNumber}`).value =
      excelCount(totals.ticketsSold)
    worksheet.getCell(`${getColNum('comp')}${totalRowNumber}`).value =
      excelCount(totals.compsSold)
    worksheet.getCell(`${getColNum('total')}${totalRowNumber}`).value =
      excelCount(totals.total)
    worksheet.getCell(`${getColNum('scanned')}${totalRowNumber}`).value =
      excelCount(totals.scanned)
    worksheet.getCell(`${getColNum('unscanned')}${totalRowNumber}`).value =
      excelCount(totals.unScanned)
    worksheet.getCell(`${getColNum('proceeds')}${totalRowNumber}`).value =
      totals.ticketProceeds

    styleTotalRow(totalRow, worksheet)

    // Count columns only: integers in Excel (no ".0" / ".00" for whole tickets).
    const qtyNumFmt = '#,##0'
    numberFormat(4, worksheet, headerRows, qtyNumFmt)
    numberFormat(5, worksheet, headerRows, qtyNumFmt)
    numberFormat(6, worksheet, headerRows, qtyNumFmt)
    numberFormat(7, worksheet, headerRows, qtyNumFmt)
    numberFormat(8, worksheet, headerRows, qtyNumFmt)
    // Proceeds: optional cents so whole dollars do not show ".00"
    numberFormat(9, worksheet, headerRows, '"$"#,##0.##;[Red]-"$"#,##0.##')

    // Generate and download Excel file
    // save excel worksheet
    saveWorkbook(workbook, 'sales and attendance report')
      .then()
      .catch((err) => alert(err.message))
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section
      className="spacer-sm"
      data-testid="event-sales-attendance-report-section"
    >
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Event Sales and Attendance Report</h1>
        </div>
        <p className="section-header-desc">
          View ticket sales and attendance details for your selected season.
        </p>
      </header>
      {seasons?.length > 0 ? (
        <>
          <ReportActions
            isLoading={isLoadingData}
            onRefresh={refresh}
            isRefreshing={isLoadingData}
            selected={selectedSeason}
            data={eventAnalytics}
            downloadReport={() =>
              handleExcelExport(
                organization?.name,
                seasons?.find(
                  (season) => String(season?.id) === String(selectedSeason)
                ),
                eventAnalytics,
                calculateTotals()
              )
            }
          >
            <SeasonSelector
              isLoading={isLoadingData}
              seasons={seasons}
              selected={selectedSeason}
              setSelected={setSelectedSeason}
            />
          </ReportActions>
          <Results
            isLoading={isLoadingData}
            selectedSeason={selectedSeason}
            data={eventAnalytics}
            calculateTotals={calculateTotals}
          />
        </>
      ) : (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>No seasons created</p>
            <p className="subtitle subtitle--dark">
              Create a season to start tracking event sales and attendance.
            </p>
          </EmptyContainer>
        </Card>
      )}
    </section>
  )
}

// Event row component
const EventRow = ({ event }) => {
  const isMonthlyTotal = event.isMonthlyTotal

  return (
    <>
      <tr className={`md-x ${isMonthlyTotal ? 'total-row light' : ''}`}>
        <td colSpan={3}>{formatEventDate(event)}</td>
        <td colSpan={3} className="text-left">
          {event.eventName}
        </td>
        <td colSpan={2}>{event.seasonName || ''}</td>
        <td className="whitespace-nowrap">{formatNumber(event.sold)}</td>
        <td className="whitespace-nowrap">{formatNumber(event.comp)}</td>
        <td className="whitespace-nowrap">{formatNumber(event.total)}</td>
        <td className="whitespace-nowrap">{formatNumber(event.scanned)}</td>
        <td className="whitespace-nowrap">{formatNumber(event.unscanned)}</td>
        <td className="whitespace-nowrap">
          {formatCurrency(event.ticketProceeds)}
        </td>
      </tr>
      {isMonthlyTotal && (
        <tr className="white">
          <td colSpan={14} className="td--empty"></td>
        </tr>
      )}
    </>
  )
}

// Shared table headers
const TableHeader = () => (
  <thead>
    <tr className="md">
      <th colSpan={3} className="uppercase text-body">
        Event date
      </th>
      <th colSpan={3} className="uppercase text-body text-left">
        Event
      </th>
      <th colSpan={2} className="uppercase text-body">
        Season
      </th>
      <th className="uppercase text-body xs">Sold</th>
      <th className="uppercase text-body xs">Comp</th>
      <th className="uppercase text-body xs">Total</th>
      <th className="uppercase text-body md">Scanned</th>
      <th className="uppercase text-body lg">Unscanned</th>
      <th className="uppercase text-body xl">Ticket proceeds</th>
    </tr>
  </thead>
)

function EventSalesAndAttendanceTable({ eventAnalytics, totals }) {
  return (
    <div className="table-container border-bottom-0">
      <div className="table-container--fixed-height">
        <Table>
          <TableHeader />
          <tbody>
            {eventAnalytics.map((event, index) => (
              <EventRow key={index} event={event} />
            ))}
          </tbody>
          <tfoot>
            <tr className="md total-row dark">
              <td colSpan={3} className="td--empty"></td>
              <td colSpan={3} className="text-left text-uppercase">
                Grand Total:
              </td>
              <td colSpan={2} className="td--empty"></td>
              <td className="whitespace-nowrap">
                {formatNumber(totals.ticketsSold)}
              </td>
              <td className="whitespace-nowrap">
                {formatNumber(totals.compsSold)}
              </td>
              <td className="whitespace-nowrap">
                {formatNumber(totals.total)}
              </td>
              <td className="whitespace-nowrap">
                {formatNumber(totals.scanned)}
              </td>
              <td className="whitespace-nowrap">
                {formatNumber(totals.unScanned)}
              </td>
              <td className="whitespace-nowrap">
                {formatCurrency(totals.ticketProceeds)}
              </td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </div>
  )
}

function Results({ isLoading, selectedSeason, data, calculateTotals }) {
  if (!selectedSeason) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing your filters</p>
          <p className="subtitle subtitle--dark">
            Select a season from the options above. Once you've made your
            selection, your report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (!data || data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No data</p>
          <p className="subtitle subtitle--dark">
            There&apos;s currently no data for the selected season. Please
            select a different season.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  const totals = calculateTotals()

  return (
    <Card body className="card--md">
      <EventSalesAndAttendanceTable eventAnalytics={data} totals={totals} />
    </Card>
  )
}
